import * as React from 'react';
import { DialogTrigger, type DialogTriggerProps, type ModalOverlayProps } from 'react-aria-components';
import { DialogContent } from './dialog.content';
import { DialogFooter } from './dialog.footer';
import { DialogHeader } from './dialog.header';
import { DialogOverlay } from './dialog.overlay';
import { DialogSubtitle } from './dialog.subtitle';
import { DialogTitle } from './dialog.title';

/**
 * Dialog component that wraps react-aria-components DialogTrigger.
 *
 * When isOpen is controlled, renders the overlay directly with isOpen/onOpenChange
 * instead of using DialogTrigger, which avoids PressResponder errors in newer
 * versions of react-aria-components.
 *
 * When isOpen is not provided (uncontrolled), uses DialogTrigger as normal.
 */
function _Dialog(props: DialogTriggerProps) {
  const { children, isOpen, onOpenChange, ...rest } = props;

  // Uncontrolled mode — use DialogTrigger directly
  if (isOpen === undefined) {
    return (
      <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} {...rest}>
        {children}
      </DialogTrigger>
    );
  }

  // Controlled mode — skip DialogTrigger to avoid PressResponder errors.
  // Pass isOpen/onOpenChange directly to the overlay.
  const childArray = React.Children.toArray(children);

  const overlay = childArray.find((child) => React.isValidElement(child) && child.type === DialogOverlay);

  const otherChildren = childArray.filter((child) => !(React.isValidElement(child) && child.type === DialogOverlay));

  return (
    <>
      {otherChildren}
      {React.isValidElement(overlay) &&
        React.cloneElement(overlay as React.ReactElement<ModalOverlayProps>, {
          isOpen,
          onOpenChange,
        })}
    </>
  );
}

const Dialog = Object.assign(_Dialog, {
  Content: DialogContent,
  Footer: DialogFooter,
  Header: DialogHeader,
  Overlay: DialogOverlay,
  Subtitle: DialogSubtitle,
  Title: DialogTitle,
});

export { Dialog };
export type { DialogTriggerProps as DialogProps };
export * from './types';
